import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import { Problem, QuestionType } from "@/types";
import { renderMathInHtml } from "@/utils/mathRenderer";

import EditingField from "./EditingField";
import ResizableRichTextEditor from "./ResizableRichTextEditor";

// Helper function to clean HTML content by removing <p> tags
const cleanHtmlContent = (html: string): string => {
  return html.replace(/<p[^>]*>(.*?)<\/p>/g, "$1");
};

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];
interface ValidationErrors {
  title?: string;
  question?: string;
  solution?: string;
  options?: string;
  correctAnswer?: string;
}

interface QuestionContentProps {
  type: QuestionType;
  problem: Problem;
  editedProblem?: Problem;
  isEditing?: boolean;
  onChange: (field: keyof Problem, value: any) => void;
  errors?: ValidationErrors;
}

const QuestionContent = ({
  type,
  problem,
  editedProblem,
  isEditing,
  onChange,
  errors,
}: QuestionContentProps) => {
  switch (type) {
    case QuestionType.MultipleChoice:
      return (
        <div>
          <Typography variant="subtitle1">Options:</Typography>
          {isEditing ? (
            <RadioGroup
              value={editedProblem?.correctAnswer}
              onChange={(e) => onChange("correctAnswer", e.target.value)}
            >
              {editedProblem?.options?.map((option, idx) => (
                <Stack direction="row" spacing={2} key={idx} pt={2}>
                  <FormControlLabel
                    value={option || LETTERS[idx]}
                    control={<Radio />}
                    label={`${LETTERS[idx]}.`}
                  />
                  <Box sx={{ width: "100%" }}>
                    <ResizableRichTextEditor
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(editedProblem?.options || [])];
                        newOptions[idx] = e;
                        onChange("options", newOptions);
                      }}
                      placeholder={`Option ${idx + 1}`}
                      initialHeight={65}
                      hideMenuBar={true}
                      sx={{
                        p: 0,
                      }}
                    />
                    {errors?.options && (
                      <FormHelperText error>{errors.options}</FormHelperText>
                    )}{" "}
                  </Box>
                </Stack>
              ))}
            </RadioGroup>
          ) : (
            <ul className="list-disc pl-5 text-sm text-gray-600">
              {problem.options?.map((option, idx) => (
                <li
                  key={idx}
                  className={
                    option === problem.correctAnswer
                      ? "font-bold text-green-600"
                      : ""
                  }
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: cleanHtmlContent(renderMathInHtml(option, problem.formatVersion)),
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    case QuestionType.TrueFalse:
      return (
        <div>
          <Typography variant="subtitle1">Options:</Typography>
          {isEditing ? (
            <RadioGroup
              value={editedProblem?.correctAnswer}
              onChange={(e) => onChange("correctAnswer", e.target.value)}
            >
              <FormControlLabel value="True" control={<Radio />} label="True" />
              <FormControlLabel
                value="False"
                control={<Radio />}
                label="False"
              />
            </RadioGroup>
          ) : (
            <ul className="list-disc pl-5 text-sm text-gray-600">
              <li
                className={
                  problem.correctAnswer === "True"
                    ? "font-bold text-green-600"
                    : ""
                }
              >
                True
              </li>
              <li
                className={
                  problem.correctAnswer === "False"
                    ? "font-bold text-green-600"
                    : ""
                }
              >
                False
              </li>
            </ul>
          )}
        </div>
      );
    case QuestionType.ShortAnswer:
      return (
        <div>
          <Box sx={{ mb: 2 }}>
            <EditingField
              hideMenuBar={true}
              label="Answer"
              value={editedProblem?.correctAnswer || ""}
              onChange={(value) => onChange("correctAnswer", value)}
              type="textarea"
              placeholder="Enter the answer"
              isEditing={isEditing}
              error={!!errors?.correctAnswer}
            />
            {errors?.correctAnswer && (
              <FormHelperText error>{errors.correctAnswer}</FormHelperText>
            )}
          </Box>
        </div>
      );
    default:
      return null;
  }
};

export default QuestionContent;
